import { useMemo, useState } from "react";
import { useAtomValue } from "jotai";
import { Plus } from "lucide-react";
import { SortingState } from "@tanstack/react-table";
import { userAtom } from "@/stores/authStore";
import { useGetAllImporters } from "@/api/importer.api";
import usePagination from "@/hooks/useSyncedPagination";
import { DataTable } from "@/components/ui/customs/data-table/data-table";
import { Button } from "@/components/ui/button";
import { getImporterColumns } from "@/components/backOffice/importer/importerTableColumns";
import { ImporterFormModal } from "@/components/backOffice/importer/ImporterFormModal";
import { ImporterDeleteModal } from "@/components/backOffice/importer/ImporterDeleteModal";
import { ImporterDto } from "@/types/api/api-types";
import { useTranslation } from "react-i18next";

export const ImporterPage = () => {
  const { t } = useTranslation("boCatalog");
  const user = useAtomValue(userAtom);

  const [sorting, setSorting] = useState<SortingState>([{ id: "name", desc: false }]);
  const [pagination, setPagination] = usePagination("importers");

  const { data: importers } = useGetAllImporters({
    take: pagination.pageSize,
    skip: pagination.pageIndex * pagination.pageSize,
    sortBy: sorting[0]?.id,
    sortOrder: sorting[0]?.desc ? "desc" : "asc",
  });

  const canManageImporters = useMemo(() => {
    return user?.role === "SUPERADMIN" || user?.role === "ADMIN";
  }, [user]);

  const [formModalOpen, setFormModalOpen] = useState(false);
  const [editImporter, setEditImporter] = useState<ImporterDto | null>(null);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteImporter, setDeleteImporter] = useState<ImporterDto | null>(null);

  const handleCreate = () => {
    setEditImporter(null);
    setFormModalOpen(true);
  };

  const handleEdit = (importer: ImporterDto) => {
    setEditImporter(importer);
    setFormModalOpen(true);
  };

  const handleCloseFormModal = () => {
    setFormModalOpen(false);
    setEditImporter(null);
  };

  const handleDelete = (importer: ImporterDto) => {
    setDeleteImporter(importer);
    setDeleteModalOpen(true);
  };

  const handleCloseDeleteModal = () => {
    setDeleteModalOpen(false);
    setDeleteImporter(null);
  };

  const columns = getImporterColumns(t, handleEdit, handleDelete, canManageImporters);

  return (
    <div className="flex h-fit w-full flex-1 flex-col gap-4 p-8">
      <div className="flex w-full flex-row items-center justify-between">
        <div className="flex flex-col gap-2">
          <h1 className="font-jakarta-sans text-3xl font-semibold">{t("importersPage.title")}</h1>
          <p>{t("importersPage.subtitle")}</p>
        </div>
        {canManageImporters && (
          <Button onClick={handleCreate}>
            <Plus className="mr-2 h-4 w-4" />
            {t("importersPage.add")}
          </Button>
        )}
      </div>
      <div className="flex flex-1 flex-col gap-6">
        <DataTable
          data={importers?.data || []}
          columns={columns}
          pagination={pagination}
          setPagination={setPagination}
          sorting={sorting}
          setSorting={setSorting}
          rowCount={importers?.count || 0}
          autoResetPageIndex={false}
        />
      </div>

      <ImporterFormModal
        open={formModalOpen}
        onClose={handleCloseFormModal}
        importer={editImporter}
      />

      {deleteImporter && (
        <ImporterDeleteModal
          open={deleteModalOpen}
          onClose={handleCloseDeleteModal}
          importer={deleteImporter}
        />
      )}
    </div>
  );
};
